import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    MenuItem,
    TextField,
    Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import AutoComplete from '@material-ui/lab/Autocomplete';
import NumberInput from '../../../../../common/components/UI/Input/Number';
import { RequestTypes, RequestSubtypes, RestrictionLimitedPeriod, DataType } from '../../../../Enums';
import { getRestrictionLimitedPeriodLabel } from '../../../../Enums/Helpers/LabellingHelper';
import { UPDATE_VALIDATION_RULE } from '../../../store/ruleSet';
import StyledSelect from '../StyledSelect';
import InputTitle from '../InputTitle';
import InputDisclaimer from '../InputDisclaimer';
import StyledSwitch from '../StyledSwitch';
import './OnRestrictionSection.scss';

/**
 * @param {{ validationErrors: {[Key: string], isDisabled: boolean} }} props
 */
function OnRestrictionSection(props) {
    const { validationErrors, isDisabled } = props;
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'settings.ruleSet.employeeRulesStep.onRestrictionRule' });
    const dispatch = useDispatch();
    /** @type {EmploymentBillboard.AdminStore} */
    // @ts-ignore
    const { adminRuleSet, adminAppData } = useSelector((state) => state);
    const { validationRuleSet, requestType, requestSubtype, } = adminRuleSet;
    const { isOnRestriction, restrictionStartAfter,
        restrictionDuration, restrictionDataviewId, restrictionDataviewKeyAlias, restrictionCustomDateFormat, restrictionIsCanMakeRequests,
        restrictionIsOnlyManualApproval, restrictionAllowAdditionalManualApprovalRequests, restrictionIsAllowRequestingVto, restrictionMaxNumberOfRequests, restrictionLimitedPeriod,
    } = validationRuleSet;
    const { dataviews, customDateFormat: customDateFormatList } = adminAppData;
    const isOpenShiftSignUp = RequestTypes.openShift === requestType && requestSubtype === RequestSubtypes.signUp;
    const isVTO = RequestTypes.voluntaryTimeOff === requestType;

    /**
    * @type {SelectDataviewFieldOption[]}
    */
    const restrictionDataviewFields = dataviews.find((x) => x.id === restrictionDataviewId)?.fields.map((x) => ({
        value: x.key,
        label: x.label,
    })) || [];

    const selectedRestrictionDataviewField = restrictionDataviewFields.find((x) => x.value === restrictionDataviewKeyAlias);
    const selectedRestrictionCustomDateFormat = customDateFormatList.find((x) => x.id === restrictionCustomDateFormat);
    /**
     * @typedef {{ label: string, value: RestrictionLimitedPeriod }} IRestrictionLimitedPeriodOption
     * @type {IRestrictionLimitedPeriodOption[]}
     */
    const restrictionLimitedPeriodOptions = Object
        .values(RestrictionLimitedPeriod)
        .map((period) => ({
            label: getRestrictionLimitedPeriodLabel(period),
            value: period,
        }));
    const selectedRestrictionLimitedPeriod = restrictionLimitedPeriodOptions.find((x) => x.value === restrictionLimitedPeriod);

    /**
     * @param {string} target
     * @param {string} value
     */
    const updateValidationRulesStore = (target, value) => {
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                [target]: value,
            },
        });
    };

    const updateIsOnRestriction = (value) => {
        let valuesToReset = null;
        if (!value) {
            valuesToReset = {
                restrictionStartAfter: null,
                restrictionDuration: null,
                restrictionDataviewId: null,
                restrictionIsCanMakeRequests: null,
                restrictionDataviewKeyAlias: '',
                restrictionCustomDateFormat: null,
                restrictionLimitedPeriod: null,
                restrictionMaxNumberOfRequests: null,
                restrictionIsAllowRequestingVto: false,
            }
        }
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                isOnRestriction: value,
                restrictionIsOnlyManualApproval: false,
                restrictionAllowAdditionalManualApprovalRequests: false,
                restrictionIsCanMakeRequests: false,
                restrictionMaxNumberOfRequests: 0,
                restrictionLimitedPeriod: RestrictionLimitedPeriod.RestrictionPeriod,
                restrictionIsAllowRequestingVto: false,
                ...valuesToReset,
            },
        });
    }

    const toggleIsCanMakeRequests = (checked) => {
        updateValidationRulesStore('restrictionIsCanMakeRequests', checked);
    }

    const toggleIsAllowRequestingVto = (checked) => {
        updateValidationRulesStore('restrictionIsAllowRequestingVto', checked);
    }

    const toggleIsOnlyManualApproval = (checked) => {
        updateValidationRulesStore('restrictionIsOnlyManualApproval', checked)
        if (checked) {
            updateValidationRulesStore('restrictionAllowAdditionalManualApprovalRequests', false)
        }
    }
    const toggleIsAllowAdditionalManualApproval = (checked) => {
        updateValidationRulesStore('restrictionAllowAdditionalManualApprovalRequests', checked)
        if (checked) {
            updateValidationRulesStore('restrictionIsOnlyManualApproval', false)
        }
    }

    return (
        <div className={`${isOnRestriction ? 'ors-on-restriction-section' : ''}`}>
            <StyledSwitch
                checked={isOnRestriction}
                onChange={(value) => updateIsOnRestriction(value)}
                label={t('title')}
                style={{ display: 'inline-flex' }}
                disabled={isDisabled}
            />
            {isOnRestriction && (<div>
                <InputTitle>{t('referenceDate.title')}</InputTitle>
                <div className="ers-dataview-info-container ors-on-restriction-reference-date">
                    <div className="rse--selection-field-container ers-one-third-space">
                        <InputTitle>{t('referenceDate.dataviewHeader')}</InputTitle>
                        <AutoComplete
                            id="dataview-select"
                            value={restrictionDataviewId !== null ? dataviews.find((x) => x.id === restrictionDataviewId) : null}
                            options={dataviews}
                            onChange={(_event, optionSelected) => updateValidationRulesStore('restrictionDataviewId', optionSelected?.id)}
                            renderInput={(params) => (<TextField
                                {...params}
                                variant="outlined"
                                placeholder={t('referenceDate.dataviewPlaceholder')}
                            />)}
                            getOptionLabel={(option) => option.name}
                            includeInputInList
                            disabled={isDisabled}
                            disableClearable
                        />
                        {
                            validationErrors.restrictionDataviewId ? <span style={{ color: 'red' }}>{t('errors.restrictionDataviewId')}</span> : null
                        }
                    </div>
                    <div className="rse--selection-field-container ers-one-third-space">
                        <InputTitle>{t('referenceDate.dataviewFieldHeader')}</InputTitle>
                        <StyledSelect
                            labelId="select-dataview-field-label"
                            id="select-dataview-field-type"
                            disabled={isDisabled}
                            renderValue={(/** @type {string} */
                                _value,
                            ) => selectedRestrictionDataviewField?.label}
                            value={restrictionDataviewKeyAlias !== null ? selectedRestrictionDataviewField : null}
                            onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                                event,
                            ) => updateValidationRulesStore('restrictionDataviewKeyAlias', event.target.value)}
                        >
                            {restrictionDataviewFields.map((x) => (
                                <MenuItem
                                    value={x.value}
                                    key={`SelectItem_${x.label}`}
                                >
                                    {x.label}
                                </MenuItem>
                            ))}
                        </StyledSelect>
                        {
                            validationErrors.restrictionDataviewKeyAlias ? <span style={{ color: 'red' }}>{t('errors.restrictionDataviewKeyAlias')}</span> : null
                        }
                    </div>
                    <div className="rse--selection-field-container ers-one-third-space">
                        <InputTitle>{t('referenceDate.dateFormatHeader')}</InputTitle>
                        <StyledSelect
                            labelId="select-data-type-label"
                            id="select-data-type-type"
                            disabled={isDisabled}
                            renderValue={(/** @type {string} */
                                _value,
                            ) => selectedRestrictionCustomDateFormat?.name}
                            value={restrictionCustomDateFormat !== null ? selectedRestrictionCustomDateFormat : null}
                            onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                                event,
                            ) => updateValidationRulesStore('restrictionCustomDateFormat', event.target.value)}
                        >
                            {customDateFormatList.filter(x => x.dataType === DataType.Date).map((x) => (
                                <MenuItem
                                    value={x.id}
                                    key={`SelectItem_${x.name}`}
                                >
                                    {x.name}
                                </MenuItem>
                            ))}
                        </StyledSelect>
                        {
                            validationErrors.restrictionCustomDateFormat ? <span style={{ color: 'red' }}>{t('errors.restrictionCustomDateFormat')}</span> : null
                        }
                    </div>
                </div>
                <InputTitle>{t('restrictionPeriod.title')}</InputTitle>
                <div className="rse--selection-field-container">
                    <Typography
                        variant="body1"
                        className={`ers-numeric-input-unit-label ${isDisabled && 'disabled'}`}
                    >
                        {t('restrictionPeriod.sentenceBeginning')}
                    </Typography>
                    <NumberInput
                        handleChangeInput={(value, target) => updateValidationRulesStore(target, value)}
                        min={0}
                        max={999}
                        step={1}
                        precision={0}
                        value={restrictionStartAfter}
                        defaultValue={1}
                        target={'restrictionStartAfter'}
                        isDisabled={isDisabled}
                        error={validationErrors.restrictionStartAfter || restrictionStartAfter < 0 || restrictionStartAfter > 999}
                    />
                    <Typography
                        variant="body1"
                        className={`ers-numeric-input-unit-label ${isDisabled && 'disabled'}`}
                    >
                        {t('restrictionPeriod.sentenceMiddle')}
                    </Typography>
                    <NumberInput
                        handleChangeInput={(value, target) => updateValidationRulesStore(target, value)}
                        min={1}
                        max={999}
                        step={1}
                        precision={0}
                        value={restrictionDuration}
                        defaultValue={1}
                        target={'restrictionDuration'}
                        isDisabled={isDisabled}
                        error={validationErrors.restrictionDuration || restrictionStartAfter > 999}
                    />
                    <Typography
                        variant="body1"
                        className={`ers-numeric-input-unit-label ${isDisabled && 'disabled'}`}
                    >
                        {t('restrictionPeriod.sentenceEnding')}
                    </Typography>
                </div>
                {
                    validationErrors.restrictionStartAfter ? <span className="rse-validation-error">{t('errors.restrictionStartAfter')}</span> : null
                }

                {
                    validationErrors.restrictionDuration ? <span className="rse-validation-error">{t('errors.restrictionDuration')}</span> : null
                }
                <InputDisclaimer>{ t('restrictionPeriod.disclaimer')}</InputDisclaimer>

                <>
                    <StyledSwitch
                        checked={restrictionIsCanMakeRequests}
                        onChange={(value) => toggleIsCanMakeRequests(value)}
                        label={t('restrictionIsCanMakeRequests.toggleLabel')}
                        disabled={isDisabled}
                    />
                </>

                {restrictionIsCanMakeRequests && (isVTO ? (
                    <>
                        <StyledSwitch
                            checked={restrictionIsAllowRequestingVto}
                            onChange={(value) => toggleIsAllowRequestingVto(value)}
                            label={t('restrictionIsAllowRequestingVto.toggleLabel')}
                            disabled={isDisabled}
                        />
                    </>
                ) : (
                    <>
                        <InputTitle>{t('restrictionMaxNumberOfRequests.title')}</InputTitle>
                        <NumberInput
                            handleChangeInput={(value, target) => updateValidationRulesStore(target, value)}
                            min={0}
                            max={99}
                            step={1}
                            precision={0}
                            value={restrictionMaxNumberOfRequests}
                            defaultValue={1}
                            target={'restrictionMaxNumberOfRequests'}
                            isDisabled={isDisabled}
                            error={isOnRestriction && (restrictionMaxNumberOfRequests < 0 || restrictionMaxNumberOfRequests > 99)}
                        />
                        <Typography
                            variant="body1"
                            className={`ers-numeric-input-unit-label ${isDisabled && 'disabled'}`}
                        >
                            {t('restrictionMaxNumberOfRequests.inputLabel')}
                        </Typography>
                        <div className="restrictionLimitedPeriodSelect">
                            <StyledSelect
                                labelId="select-restrictionLimitedPeriod-label"
                                id="select-restrictionLimitedPeriod-type"
                                disabled={isDisabled}
                                renderValue={(/** @type {string} */
                                    _value,
                                ) => selectedRestrictionLimitedPeriod?.label}
                                value={restrictionLimitedPeriod !== null ? selectedRestrictionLimitedPeriod : null}
                                onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                                    event,
                                ) => updateValidationRulesStore('restrictionLimitedPeriod', event.target.value)}
                            >
                                {restrictionLimitedPeriodOptions.map((x) => (
                                    <MenuItem
                                        value={x.value}
                                        key={`SelectItem_${x.label}`}
                                    >
                                        {x.label}
                                    </MenuItem>
                                ))}
                            </StyledSelect>
                        </div>
                        <InputDisclaimer>{t('restrictionMaxNumberOfRequests.disclaimer')}</InputDisclaimer>
                    </>
                ))}

                {
                    restrictionIsCanMakeRequests && ( !isOpenShiftSignUp && (<>
                        <StyledSwitch
                            checked={restrictionIsOnlyManualApproval}
                            onChange={(value) => toggleIsOnlyManualApproval(value)}
                            label={t('restrictionIsOnlyManualApproval.toggleLabel')}
                            disabled={isDisabled}
                        />
                        {!isVTO && (
                            <>
                                <StyledSwitch
                                    checked={restrictionAllowAdditionalManualApprovalRequests}
                                    onChange={(value) => toggleIsAllowAdditionalManualApproval(value)}
                                    label={t('restrictionAllowAdditionalManualApprovalRequests.toggleLabel')}
                                    disabled={isDisabled}
                                />
                                <InputDisclaimer>{t('restrictionAllowAdditionalManualApprovalRequests.disclaimer')}</InputDisclaimer>
                            </>
                        )}
                    </>
                    ))
                }
            </div>)}
        </div>);
}

export default OnRestrictionSection;